/**
 * ScarRegister (C-8) — Supplier Corrective Action Request register.
 * KPI cards + status/severity filters + table + "Issue SCAR" dialog.
 * Route: /scars
 */
import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { PageHero } from '@edms/design-system';
import styles from '../Recalls/Recalls.module.css';
import {
  useScarsQuery, useCreateScar, useSupplierPickerQuery,
  SCAR_STATUS_LABEL, SCAR_SOURCE_LABEL, SCAR_SEVERITY_LABEL, type NewScarInput,
} from './useScar';

function fmtDate(iso: string | null | undefined) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('en-GB', { year: 'numeric', month: 'short', day: '2-digit' }); }
  catch { return String(iso); }
}
function sevClass(s: string) {
  return s === 'CRITICAL' ? styles.cls1 : s === 'MAJOR' ? styles.cls2 : styles.cls3;
}

export function ScarRegister() {
  const navigate = useNavigate();
  const [status, setStatus] = useState('');
  const [severity, setSeverity] = useState('');
  const q = useScarsQuery({ status: status || undefined, severity: severity || undefined });
  const create = useCreateScar();
  const suppliers = useSupplierPickerQuery();

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<NewScarInput>({ supplierId: '', title: '', source: 'AUDIT_FINDING', severity: 'MAJOR' });
  const [err, setErr] = useState<string | null>(null);

  const items = q.data?.items ?? [];
  const stats = q.data?.stats;

  async function submit() {
    setErr(null);
    if (!form.supplierId) { setErr('Select a supplier.'); return; }
    if (!form.title.trim()) { setErr('A title is required.'); return; }
    try {
      const created = await create.mutateAsync(form);
      setOpen(false);
      setForm({ supplierId: '', title: '', source: 'AUDIT_FINDING', severity: 'MAJOR' });
      navigate({ to: '/scars/$id', params: { id: created.scarId } });
    } catch (e: unknown) { setErr(e instanceof Error ? e.message : 'Could not issue the SCAR.'); }
  }

  return (
    <main className={styles.page}>
      <PageHero
        eyebrow="Suppliers · Quality Events"
        title="Supplier Corrective Actions (SCAR)"
        subtitle="Formal corrective-action requests issued to suppliers — root-cause & CAPA response, review, and closure. Triggered by audit findings, complaints, deviations, SCNs, or incoming inspection."
        actions={
          <button type="button" className={`${styles.btn} ${styles.btnPrimary}`} onClick={() => { setOpen(true); setErr(null); }}>
            Issue SCAR
          </button>
        }
      />

      {stats && (
        <div className={styles.kpiRow}>
          <div className={styles.kpi}><span className={styles.kpiNum}>{stats.open}</span><span className={styles.kpiLabel}>Open</span></div>
          <div className={`${styles.kpi} ${Number(stats.critical_open) > 0 ? styles.kpiAlert : ''}`}><span className={styles.kpiNum}>{stats.critical_open}</span><span className={styles.kpiLabel}>Critical open</span></div>
          <div className={`${styles.kpi} ${Number(stats.overdue) > 0 ? styles.kpiAlert : ''}`}><span className={styles.kpiNum}>{stats.overdue}</span><span className={styles.kpiLabel}>Overdue</span></div>
          <div className={styles.kpi}><span className={styles.kpiNum}>{stats.closed}</span><span className={styles.kpiLabel}>Closed</span></div>
        </div>
      )}

      <div className={styles.filters}>
        <select aria-label="Status filter" value={status} onChange={(e) => setStatus(e.target.value)} className={styles.select}>
          <option value="">All statuses</option>
          {Object.keys(SCAR_STATUS_LABEL).map((s) => <option key={s} value={s}>{SCAR_STATUS_LABEL[s]}</option>)}
        </select>
        <select aria-label="Severity filter" value={severity} onChange={(e) => setSeverity(e.target.value)} className={styles.select}>
          <option value="">All severities</option>
          {Object.keys(SCAR_SEVERITY_LABEL).map((s) => <option key={s} value={s}>{SCAR_SEVERITY_LABEL[s]}</option>)}
        </select>
        <span className={styles.resultCount}>{items.length} SCAR{items.length === 1 ? '' : 's'}</span>
      </div>

      {q.isLoading ? (
        <div className={styles.empty}>Loading SCARs…</div>
      ) : items.length === 0 ? (
        <div className={styles.empty}>No SCARs match the current filters.</div>
      ) : (
        <table className={styles.table}>
          <thead>
            <tr><th>SCAR</th><th>Supplier</th><th>Title</th><th>Source</th><th>Severity</th><th>Status</th><th>Response due</th></tr>
          </thead>
          <tbody>
            {items.map((s) => (
              <tr key={s.scarId} className={styles.row} onClick={() => navigate({ to: '/scars/$id', params: { id: s.scarId } })}>
                <td className={styles.mono}>{s.scarNumber}</td>
                <td>{s.supplierName ?? '—'}</td>
                <td>{s.title}</td>
                <td>{SCAR_SOURCE_LABEL[s.source] ?? s.source}</td>
                <td><span className={`${styles.pill} ${sevClass(s.severity)}`}>{SCAR_SEVERITY_LABEL[s.severity] ?? s.severity}</span></td>
                <td><span className={styles.statusPill}>{SCAR_STATUS_LABEL[s.status] ?? s.status}</span></td>
                <td>{fmtDate(s.responseDueDate)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {open && (
        <div className={styles.modalBackdrop} role="dialog" aria-modal aria-label="Issue SCAR">
          <div className={styles.modal}>
            <h2 className={styles.modalTitle}>Issue a SCAR</h2>
            {err && <div className={styles.err} role="alert">{err}</div>}
            <label className={styles.field}><span>Supplier *</span>
              <select value={form.supplierId} onChange={(e) => setForm({ ...form, supplierId: e.target.value })}>
                <option value="">Select a supplier…</option>
                {(suppliers.data ?? []).map((s) => <option key={s.id} value={s.id}>{s.name} ({s.criticality})</option>)}
              </select></label>
            <label className={styles.field}><span>Title *</span>
              <input type="text" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Out-of-spec assay on incoming API lot" /></label>
            <div className={styles.field2}>
              <label className={styles.field}><span>Source</span>
                <select value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })}>
                  {Object.keys(SCAR_SOURCE_LABEL).map((s) => <option key={s} value={s}>{SCAR_SOURCE_LABEL[s]}</option>)}
                </select></label>
              <label className={styles.field}><span>Severity</span>
                <select value={form.severity} onChange={(e) => setForm({ ...form, severity: e.target.value })}>
                  {Object.keys(SCAR_SEVERITY_LABEL).map((s) => <option key={s} value={s}>{SCAR_SEVERITY_LABEL[s]}</option>)}
                </select></label>
            </div>
            <label className={styles.field}><span>Response due</span>
              <input type="date" value={form.responseDueDate ?? ''} onChange={(e) => setForm({ ...form, responseDueDate: e.target.value })} /></label>
            <label className={styles.field}><span>Description</span>
              <textarea rows={3} value={form.description ?? ''} onChange={(e) => setForm({ ...form, description: e.target.value })} /></label>
            <div className={styles.modalActions}>
              <button type="button" className={`${styles.btn} ${styles.btnNeutral}`} onClick={() => setOpen(false)}>Cancel</button>
              <button type="button" className={`${styles.btn} ${styles.btnPrimary}`} onClick={() => void submit()} disabled={create.isPending}>
                {create.isPending ? 'Issuing…' : 'Issue SCAR'}
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}
